import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";

import { getInitialSegment, segments } from "./config/segments";
import { AssetIntelligencePage } from "./pages/AssetIntelligencePage";
import { DashboardPage } from "./pages/DashboardPage";
import { LoginPage } from "./pages/LoginPage";
import { ModulePage } from "./pages/ModulePage";
import { TechnicianAppPage } from "./pages/TechnicianAppPage";
import { AccessManagementPage } from "./pages/AccessManagementPage";
import { CompanyManagementPage } from "./pages/CompanyManagementPage";
import { ServiceDeskWorkspacePage } from "./pages/ServiceDeskWorkspacePage";
import { InfrastructureTopologyPage } from "./pages/InfrastructureTopologyPage";
import { ClientPortalPage } from "./pages/ClientPortalPage";
import { ClientStructurePage } from "./pages/ClientStructurePage";
import { PwaInstallPage } from "./pages/PwaInstallPage";
import { TechnicianMonitoringPage } from "./pages/TechnicianMonitoringPage";
import { TechnicianFieldWorkspacePage } from "./pages/TechnicianFieldWorkspacePage";
import { InstitutionalSite } from "./pages/InstitutionalSite";
import { getPwaConfig, getPwaMode } from "./config/pwa";
import { getCurrentSession, logout } from "./services/api";

const modules = [
  { path: "/companies", label: "Empresas", permission: "*", platformOnly: true },
  { path: "/dashboard", label: "Dashboard", permission: "dashboard.read" },
  { path: "/cliente", label: "Portal do cliente", permission: "service_orders.read" },
  { path: "/clients", label: "Clientes", permission: "clients.read" },
  { path: "/client-structure", label: "Cadastro tecnico", permission: "clients.read" },
  { path: "/environments", label: "Ambientes", permission: "environments.read" },
  { path: "/assets", label: "Ativos", permission: "assets.read" },
  { path: "/connections", label: "Conexões DE→PARA", permission: "connections.read" },
  { path: "/service-orders", label: "Ordens de serviço", permission: "service_orders.read" },
  { path: "/technicians", label: "Técnicos", permission: "service_orders.read" },
  { path: "/tecnico", label: "App técnico", permission: "technical.vehicle_km.create" },
  { path: "/access-management", label: "Usuários e permissões", permission: "iam.access.read" },
  { path: "/topology", label: "Topologia / DE-PARA", permission: "topology.read" },
  { path: "/evidence", label: "Evidências", permission: "evidence.read" },
  { path: "/knowledge", label: "Conhecimento", permission: "knowledge.read" },
  { path: "/digital-twin", label: "Digital Twin", permission: "digital_twin.read" },
  { path: "/assistant", label: "Assistente técnico", permission: "assistant.use" },
  { path: "/netops", label: "NetOps", permission: "netops.read" },
  { path: "/monitoring", label: "Monitoramento", permission: "monitoring.read" },
  { path: "/integrations", label: "Integrações", permission: "integrations.read" },
  { path: "/reports", label: "Relatórios", permission: "reports.view" },
  { path: "/governance", label: "Aprovações", permission: "governance.approve" },
  { path: "/settings", label: "Configurações", permission: "settings.read" }
];

const accessLabels = {
  MANAGEMENT: "Gerencial",
  SUPERVISION: "Supervisão",
  OPERATIONAL: "Operacional"
};

function hasPermission(user, permission) {
  return user.permissions.some((granted) => {
    if (granted === "*") return true;
    if (permission.startsWith("technical.") && ["operations.*", "operations.records.create"].includes(granted)) return true;
    const pattern = granted.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replaceAll("*", ".*");
    return new RegExp(`^${pattern}$`).test(permission);
  });
}

function Shell({ user, onSignedOut }) {
  const navigate = useNavigate();
  const pwaMode = getPwaMode();
  const [activeSegment, setActiveSegment] = useState(getInitialSegment);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const modePaths = {
    global: null,
    campo: new Set(["/tecnico", "/service-orders", "/knowledge"]),
    cliente: new Set(["/cliente", "/service-orders", "/knowledge"])
  };
  const visibleModules = modules.filter(({ path, permission, platformOnly }) => hasPermission(user, permission) && (!platformOnly || user.roles.includes("superuser")) && (!modePaths[pwaMode] || modePaths[pwaMode].has(path)));
  const preferredHome = getPwaConfig().home;
  const defaultPath = visibleModules.some(({ path }) => path === preferredHome) ? preferredHome : visibleModules[0]?.path ?? "/access-denied";

  function changeSegment(event) {
    const segment = segments.find(({ code }) => code === event.target.value) ?? segments[0];
    localStorage.setItem("well_active_segment", segment.code);
    setActiveSegment(segment);
  }

  async function signOut() {
    setSigningOut(true);
    setSignOutError("");
    try {
      await logout();
      onSignedOut();
      navigate("/login", { replace: true });
    } catch {
      setSignOutError("Não foi possível encerrar a sessão com segurança.");
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="app-shell" style={{ "--discipline-color": activeSegment.color }}>
      <aside className="sidebar">
        <div className="brand">
          <strong>Well <em>{activeSegment.name}</em></strong>
          <span>Inteligência que conecta operações</span>
        </div>
        <label className="segment-picker">
          <span>Disciplina ativa</span>
          <select value={activeSegment.code} onChange={changeSegment}>
            {segments.map((segment) => (
              <option key={segment.code} value={segment.code}>{segment.displayName}</option>
            ))}
          </select>
        </label>
        <nav aria-label="Navegação principal">
          {visibleModules.map(({ path, label }) => (
            <NavLink key={path} to={path} className={({ isActive }) => isActive ? "active" : ""}>
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="workspace">
        <header className="topbar">
          <div>
            <span className="eyebrow">Ambiente</span>
            <strong>{pwaMode === "cliente" ? "IVO BARBOZA ADVOGADOS" : pwaMode === "campo" ? "Operação de campo" : "WellTech Global"}</strong>
          </div>
          <div className="topbar-actions">
            <span className="discipline-badge">{activeSegment.displayName}</span>
            <span className="access-badge">{accessLabels[user.access_level] ?? user.access_level}</span>
            <span className="account-name">{user.full_name ?? user.email}</span>
            {signOutError ? <span className="signout-error" role="alert">{signOutError}</span> : null}
            <button type="button" className="secondary" onClick={signOut} disabled={signingOut}>
              {signingOut ? "Saindo…" : "Sair"}
            </button>
          </div>
        </header>
        <div className="page">
          <Routes>
            {hasPermission(user, "dashboard.read") ? <Route path="/dashboard" element={<DashboardPage />} /> : null}
            {hasPermission(user, "assets.read") ? <Route path="/assets/:assetId" element={<AssetIntelligencePage />} /> : null}
            <Route path="/tecnico" element={<TechnicianFieldWorkspacePage user={user} />} />
            <Route path="/technicians" element={<TechnicianMonitoringPage />} />
            <Route path="/access-management" element={<AccessManagementPage />} />
            <Route path="/service-orders" element={<ServiceDeskWorkspacePage />} />
            <Route path="/cliente" element={<ClientPortalPage user={user} />} />
            <Route path="/client-structure" element={<ClientStructurePage />} />
            <Route path="/topology" element={<InfrastructureTopologyPage />} />
            {user.roles.includes("superuser") ? <Route path="/companies" element={<CompanyManagementPage />} /> : null}
            {visibleModules.filter(({ path }) => path !== "/dashboard").map(({ path, label }) => (
              ["/tecnico", "/technicians", "/cliente", "/client-structure", "/access-management", "/companies", "/service-orders", "/topology"].includes(path) ? null : <Route key={path} path={path} element={<ModulePage title={label} />} />
            ))}
            <Route path="/access-denied" element={<ModulePage title="Sem módulos liberados" />} />
            <Route path="*" element={<Navigate to={defaultPath} replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState({ status: "loading", user: null });

  useEffect(() => {
    let active = true;
    getCurrentSession()
      .then((user) => active && setSession({ status: "authenticated", user }))
      .catch((error) => {
        if (active) {
          setSession({ status: error.status === 401 ? "anonymous" : "unavailable", user: null });
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const expireSession = () => setSession({ status: "anonymous", user: null });
    window.addEventListener("well:session-expired", expireSession);
    return () => window.removeEventListener("well:session-expired", expireSession);
  }, []);

  if (import.meta.env.DEV && window.location.pathname === "/preview/ivo-topology") {
    return <main className="infra-preview-shell"><InfrastructureTopologyPage /></main>;
  }

  if (import.meta.env.DEV && window.location.pathname === "/preview/tecnico") {
    return <main className="technician-preview-shell"><TechnicianAppPage user={{ full_name: "Carlos Técnico", email: "tecnico@welltech.com.br", permissions: ["*"] }} /></main>;
  }

  if (import.meta.env.DEV && window.location.pathname === "/preview/chamados") {
    return <main className="ticket-preview-shell"><ServiceDeskWorkspacePage /></main>;
  }

  if (import.meta.env.DEV && window.location.pathname === "/preview/pwa-cliente") {
    return <main className="client-preview-shell"><ClientPortalPage user={{ full_name: "Mariana Barboza" }} /></main>;
  }

  if (import.meta.env.DEV && window.location.pathname === "/preview/tecnicos-monitoramento") {
    return <main className="tech-monitor-preview"><TechnicianMonitoringPage /></main>;
  }

  if (import.meta.env.DEV && window.location.pathname === "/preview/campo-atendimento") {
    return <main className="field-preview"><TechnicianFieldWorkspacePage /></main>;
  }

  const institutionalHosts = new Set(["www.welltech.net.br", "welltech.net.br", "www.well.net.br", "well.net.br"]);
  const institutionalPaths = new Set(["/", "/empresa", "/plataforma", "/clientes", "/fabricantes", "/conteudos", "/contato"]);
  const isInstitutionalPath = institutionalPaths.has(window.location.pathname) || window.location.pathname.startsWith("/solucoes/");
  const isInstitutionalSite = institutionalHosts.has(window.location.hostname.toLowerCase()) && isInstitutionalPath;
  if (isInstitutionalSite || (import.meta.env.DEV && (window.location.pathname === "/preview/site" || isInstitutionalPath))) {
    return <InstitutionalSite />;
  }
  if (window.location.pathname === "/instalar" || (import.meta.env.DEV && window.location.pathname === "/preview/instalar")) {
    return <PwaInstallPage />;
  }

  if (session.status === "loading") {
    return <main className="session-loading" aria-live="polite">Validando acesso seguro…</main>;
  }

  if (session.status === "unavailable") {
    return (
      <main className="session-loading" role="alert">
        O serviço de acesso está indisponível. Atualize a página em alguns instantes.
      </main>
    );
  }

  if (session.status === "anonymous") {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onAuthenticated={(user) => setSession({ status: "authenticated", user })} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to={getPwaConfig().home} replace />} />
      <Route path="/*" element={<Shell user={session.user} onSignedOut={() => setSession({ status: "anonymous", user: null })} />} />
    </Routes>
  );
}
